import Tooltip from '@mui/material/Tooltip';
import {
    GridEditInputCell,
    useGridApiContext,
    useGridRootProps,
    type GridRenderEditCellParams
} from '../lib/dataGrid';
import { useRecentValueEntry } from './recentValueEntry';

/**
 * Grid edit cell that surfaces a validation error as an inline tooltip (the
 * cell also gets MUI's `Mui-error` state for styling). The error is usually
 * injected by the column's `preProcessEditCellProps`, but it can also be passed
 * explicitly. Pair it with a column `renderEditCell`.
 *
 * Carries the F2 "the one from a moment ago" entry where the column offers it —
 * see ./recentValueEntry.
 *
 * In row editing it hands every keystroke on at once, as the bare cells do
 * there (see `withImmediateRowEdits` in lib/dataGrid).
 */
export default function ValidatedGridEditCell(
    props: GridRenderEditCellParams & {
        error?: string | null;
        /** The last values of this field, newest first. */
        recent?: readonly string[];
        debounceMs?: number;
    }
) {
    const { error, hasFocus, recent, id, field, value, debounceMs, ...rest } =
        props;
    const apiRef = useGridApiContext();
    const { editMode } = useGridRootProps();
    // No button in a cell: a grid column is 130-190pt wide, and an icon costs a
    // quarter of what is left to type in. The keys carry it here.
    const { onKeyDown, menu } = useRecentValueEntry({
        recent,
        value: String(value ?? ''),
        onPick: (picked) =>
            apiRef.current.setEditCellValue({ id, field, value: picked })
    });

    return (
        <Tooltip
            open={!!error && hasFocus}
            title={error ?? ''}
            arrow
            placement="bottom"
        >
            <span style={{ display: 'flex', width: '100%' }}>
                <GridEditInputCell
                    {...rest}
                    id={id}
                    field={field}
                    value={value}
                    hasFocus={hasFocus}
                    debounceMs={
                        debounceMs ?? (editMode === 'row' ? 0 : undefined)
                    }
                    onKeyDown={onKeyDown}
                    // Sets `Mui-error` on the input, which the theme lifts
                    // onto the whole cell as the red tint.
                    error={!!error}
                />
                {menu}
            </span>
        </Tooltip>
    );
}
